import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { MaterialUnit } from "fe-shared/src/types/domain";

import {
  TransferDetailsLocationsSection,
  FlagComments,
  TransferContentSection,
} from "fe-shared-transactions/src/components";
import {
  Transfer,
  TransferType,
  TransactionSection,
  TransactionFlag,
} from "fe-shared-transactions/src/types";

interface Props {
  transfer: Transfer;
  canEditTransfer?: boolean;
}
const TRANSFER_CONFIG = {
  [TransferType.IntoProcessing]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: true,
    isWithDocuments: true,
    isWithNonEligibles: false,
  },
  [TransferType.OutOfProcessing]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: false,
    isWithDocuments: true,
    isWithNonEligibles: false,
  },
  [TransferType.NonEligibleWriteOff]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: true,
    isWithDocuments: true,
    isWithNonEligibles: false,
  },
  [TransferType.NonEligibleBufferWriteOff]: {
    unit: MaterialUnit.Kg,
    isSourceWarehouse: true,
    isWithDocuments: true,
    isWithNonEligibles: true,
  },
  [TransferType.PickupFromStorage]: {
    unit: MaterialUnit.Units,
    isSourceWarehouse: true,
    isWithDocuments: false,
    isWithNonEligibles: false,
  },
  [TransferType.DeliveryToStorage]: {
    unit: MaterialUnit.Units,
    isSourceWarehouse: false,
    isWithDocuments: false,
    isWithNonEligibles: false,
  },
};

const TransferContentView = ({ transfer, canEditTransfer }: Props) => {
  const isStaffView = true;
  const {
    sourceWarehouse,
    targetWarehouse,
    transferType,
    transferFlags = [],
    nonEligibleMaterial,
  } = transfer;
  const flagsBySection = useMemo(
    () =>
      Object.values(TransactionSection).reduce((result, s) => {
        result[s] = transferFlags.filter((f) => f.sectionCode === s);
        return result;
      }, {} as { [key in TransactionSection]: TransactionFlag[] }),
    [transferFlags]
  );
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const getFlagComments = (sectionCode: TransactionSection) => (
    <FlagComments flags={flagsBySection[sectionCode]} />
  );

  const {
    unit,
    isSourceWarehouse,
    isWithDocuments,
    isWithNonEligibles,
  } = TRANSFER_CONFIG[transferType];
  const storageWarehouse = isSourceWarehouse
    ? sourceWarehouse
    : targetWarehouse;

  return (
    <>
      <TransferDetailsLocationsSection
        transfer={transfer}
        isStaffView={isStaffView}
        flags={getFlagComments(TransactionSection.Locations)}
      />
      <TransferContentSection
        unit={unit}
        transfer={transfer}
        isStaffView={isStaffView}
        isWithDocuments={isWithDocuments}
        isWithNonEligibles={isWithNonEligibles}
        canEditTransfer={canEditTransfer && transfer.isInClosurePeriod}
        location={storageWarehouse?.location}
        isActive={storageWarehouse?.isActive}
        materialName={nonEligibleMaterial?.materialName}
        showComparisonMaterials={searchParams.has("showCompare")}
      />
      {getFlagComments(TransactionSection.Data)}
    </>
  );
};

export default TransferContentView;
